<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-card>
        <div slot="header">
          <h1>Planer - Neue Werbekampagne erstellen</h1>
          <div class="card-header-actions"></div>
        </div>
        <b-row>
          <b-col lg="8">
            <b-form novalidate>
              <!-- === Title of Ad-Campaign === -->
              <b-form-group
                id="plannerInputGroup1"
                label="Titel der Werbekampagne *"
                label-for="plannertitle"
                description="(Der Titel Ihrer Werbekampagne muss zwischen 3 und 64 Zeichen lang sein)"
              >
                <b-form-input
                  :state="chkState('plannertitle')"
                  id="plannertitle"
                  type="text"
                  placeholder="Kampagnentitel"
                  @click="$v.form.plannertitle.$reset()"
                  v-model.trim="form.plannertitle"
                />
                <b-form-invalid-feedback :state="chkState('plannertitle')">
                  Pflichtfeld. Der Titel muss zwischen 3 und 64 Zeichen lang
                  sein.
                </b-form-invalid-feedback>
              </b-form-group>

              <!-- === Confirmed Advertisements === -->
              <b-form-group
                id="advertisementPlannerInputGroup1"
                label="Verfügbare aktive Werbungen"
                label-for="plannerAdvertisements"
              >
                <v-select
                  id="plannerAdvertisements"
                  ref="plannerAdvertisements"
                  :class="{
                    'error-border':
                      $v.form.plannerads.$dirty && $v.form.plannerads.$invalid,
                    'success-border':
                      $v.form.plannerads.$dirty && !$v.form.plannerads.$invalid,
                  }"
                  @search:focus="$v.form.plannerads.$reset()"
                  :state="chkState('plannerads')"
                  v-model="form.plannerads"
                  :options="getConfirmedAdvertisementsNames"
                  multiple
                  placeholder="Wählen Sie die gewünschten Werbungen aus"
                >
                  <span slot="no-options"
                    >Keine passenden Einträge gefunden</span
                  >
                </v-select>
                <b-form-invalid-feedback :state="chkState('plannerads')">
                  Pflichtfeld. Bitte wählen Sie mindestens 1 Werbung aus.
                </b-form-invalid-feedback>
              </b-form-group>

              <!-- === Available Publicitygroups === -->
              <b-form-group
                id="advertisementPlannerInputGroup2"
                label="Verfügbare Werbegruppen"
                label-for="plannerPublicitygroups"
              >
                <v-select
                  id="plannerPublicitygroups"
                  :class="{
                    'error-border':
                      $v.form.plannergroups.$dirty &&
                      $v.form.plannergroups.$invalid,
                    'success-border':
                      $v.form.plannergroups.$dirty &&
                      !$v.form.plannergroups.$invalid,
                  }"
                  ref="plannerPublicitygroups"
                  v-model="form.plannergroups"
                  @search:focus="$v.form.plannergroups.$reset()"
                  :options="getPublicitygroupNamesInclAll"
                  :state="chkState('plannergroups')"
                  multiple
                  placeholder="Wählen Sie die Zielgruppen aus"
                  ><span slot="no-options"
                    >Keine passenden Einträge gefunden</span
                  ></v-select
                >
                <b-form-invalid-feedback :state="chkState('plannergroups')">
                  Pflichtfeld. Bitte wählen Sie mindestens 1 Gruppe aus.
                </b-form-invalid-feedback>
              </b-form-group>

              <!-- === DatePicker === -->
              <b-form-group
                id="advertisementPlannerInputGroup3"
                label="Ausstrahlungs-Zeitraum der Werbung"
                label-for="plannerDatePicker"
              >
                <v-date-picker
                  mode="date"
                  v-model="form.plannerperiod"
                  :min-date="minDate"
                  :max-date="maxDate"
                  is-range
                  locale="de-at"
                  v-if="licenseExpirationLoaded"
                />
                <!-- StartDatum -->
                <div class="planner-date-display">
                  gewähltes Startdatum:
                  {{ plannerStartDate }}
                </div>
                <!-- EndDatum -->
                <div class="planner-date-display">
                  gewähltes Enddatum:
                  {{ plannerEndDate }}
                </div>
                <!-- Dauer (in Tagen) -->
                <div class="planner-date-display">
                  Gesamtdauer (in Tagen):
                  {{
                    Math.round(
                      (form.plannerperiod.end - form.plannerperiod.start) /
                        (1000 * 60 * 60 * 24)
                    ) + 1
                  }}
                </div>
              </b-form-group>

              <!-- === Buttons === -->
              <b-button
                type="button"
                @click="onSubmit"
                variant="primary"
                :disabled="licenseExpired"
              >
                {{
                  form.plannerads.length > 1 || form.plannergroups.length > 1
                    ? "Pläne erstellen"
                    : "Plan erstellen"
                }}
              </b-button>
              <b-button
                class="ml-1"
                type="button"
                @click="onReset"
                variant="danger"
                >{{
                  form.plannerads.length > 1 || form.plannergroups.length > 1
                    ? "Pläne verwerfen"
                    : "Plan verwerfen"
                }}</b-button
              >
            </b-form>
          </b-col>
        </b-row>
      </b-card>

      <!-- === Publicitygroups Overview === -->
      <b-card header-tag="header" footer-tag="footer">
        <div slot="header">
          <h1>Planer - Übersicht</h1>
          <div class="card-header-actions"></div>
        </div>
        <b-card-body>
          <v-client-table
            v-if="getPlanStatus"
            :columns="columns"
            :data="getPlanOverview"
            :options="options"
            :theme="theme"
            id="dataTablePublicitygroups"
            ref="publicitygroupOverviewTable"
          >
            <!-- ADVERTISEMENT -->
            <div
              slot="planneradvertisement"
              slot-scope="{ row }"
              @click="
                loadAdvertisementMediaURL({
                  advertisementid: getAdvertisementIdByTitle(
                    row.planneradvertisement
                  ),
                });
                displayUserHint();
              "
            >
              {{ row.planneradvertisement }}
              <i
                class="cil-hand-point-up"
                style="font-size: x-large; margin-left: 1vw; margin-top: 0.5vh"
              />
            </div>

            <!-- TYPE -->
            <div slot="plannertype" slot-scope="{ row }">
              <template v-if="row.plannertype === 'Image'"
                ><i class="cil-image1" style="font-size: x-large"></i
              ></template>
              <template v-else
                ><i class="cil-video" style="font-size: x-large"></i
              ></template>
            </div>
            <!-- STARTDATE -->
            <div slot="plannerstartdate" slot-scope="{ row }">
              {{
                new Date(row.plannerstartdate)
                  .toLocaleString("de-AT")
                  .split(",")[0]
              }}
            </div>
            <!-- ENDDATE -->
            <div slot="plannerenddate" slot-scope="{ row }">
              {{
                new Date(row.plannerenddate)
                  .toLocaleString("de-AT")
                  .split(",")[0]
              }}
            </div>
            <div slot="settings" slot-scope="props">
              <b-dropdown variant="link" size="lg" no-caret>
                <template slot="button-content">
                  &#x2699;<span class="sr-only">Search</span>
                </template>
                <b-dropdown-item
                  :disabled="
                    (props.row.plannerstatus === 'Ausgestrahlt' && props.row.Publicity_id !== 11) ||
                    (new Date(props.row.plannerenddate) < new Date() && props.row.Publicity_id !== 11)
                  "
                  @click="gotoRoute(props.row)"
                  >Details/Bearbeiten</b-dropdown-item
                >
                <b-dropdown-item
                  :disabled="props.row.plannerstatus === 'Ausgestrahlt' && props.row.Publicity_id !== 11"
                  @click="deletePlanner({ plannerid: props.row.plannerid })"
                  >Löschen</b-dropdown-item
                >
              </b-dropdown>
            </div>
          </v-client-table>
          <hr />
          <span class="note"
            >Hinweis: Aktive Werbekampagnen können weder bearbeitet, noch
            gelöscht werden.</span
          >
        </b-card-body>
      </b-card>
    </div>
  </div>

</template>

<script>
import Vue from "vue";
import { ClientTable, Event } from "vue-tables-2";
import { mapActions, mapGetters } from "vuex";
import vSelect from "vue-select";
import { DatePicker } from "v-calendar";

// VUE izitoast
import VueIziToast from "vue-izitoast";
import "izitoast/dist/css/iziToast.min.css";
Vue.use(VueIziToast);

import NotificationMixin from "../../mixins/notificationmixin";

import { validationMixin } from "vuelidate";
import { required, maxLength, minLength } from "vuelidate/lib/validators";

Vue.use(ClientTable);

const tomorrow = new Date().setDate(new Date().getDate() + 1);

const formShape = {
  plannertitle: "",
  plannerads: [],
  plannergroups: [],
  plannerperiod: {
    start: new Date(tomorrow),
    end: new Date(tomorrow),
  },
};

export default {
  name: "Advertisement-Planner",
  computed: {
    ...mapGetters([
      "getAdvertisementStatus",
      "getConfirmedAdvertisementsNames",
      "getPublicitygroupStatus",
      "getPublicitygroupNamesInclAll",
      "getPlanOverview",
      "getPlanStatus",
      "getLicenseExpiration",
      "licenseExpired",
      "getAdvertisementIdByTitle",
    ]),
    plannerStartDate() {
      let d = this.form.plannerperiod.start;
      return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
    },
    plannerEndDate() {
      let d = this.form.plannerperiod.end;
      return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
    },
    isValid() {
      return !this.$v.form.$invalid;
    },
  },
  methods: {
    ...mapActions([
      "loadPublicitygroups",
      "loadAdvertisements",
      "loadPlans",
      "submitPlans",
      "deletePlan",
      "loadAdvertisementMediaURL",
    ]),
    displayUserHint() {
      this.report(
        `Klicken Sie auf das Vorschaufenster um dieses zu schließen`,
        this.$toast
      );
    },
    async onSubmit(e) {
      e.preventDefault();
      // force validations at this point
      this.$v.$touch();

      // if already submitted, prevent from re-submitting;
      // if one wants to submit another ad, first click 'dismiss'
      if (this.submitted === true) {
        this.notify(
          1,
          ``,
          `Der Beitrag wurde bereits abgeschickt.
          Bitte wählen Sie erst "Verwerfen" um eine andere/weitere Werbekampagne zu erstellen.`,
          this.$toast
        );
        return;
      }
      // only accept submission, once all fields are valid
      if (this.isValid) {
        let err = await this.submitPlans(this.form);
        this.notify(
          err,
          `Die Werbekampagne wurde erfolgreich angelegt!`,
          `Die Werbekampagne konnte aufgrund einer Terminkollision nicht angelegt werden!`,
          this.$toast
        );
        // if submission is successful, set submitted flag
        if (!err) {
          this.submitted = true;
        }
      }
    },
    gotoRoute(plan) {
      this.$router.push({
        name: "Werbung-Planer-Bearbeiten",
        params: { plan },
      });
    },
    onReset(e) {
      e.preventDefault();
      // Reset validation
      this.submitted = false;
      // this.$nextTick(() => {
      this.$v.$reset();
      this.feedBack = "secondary";
      // in order to correctly make use of vue-select-components reactivity, we came up with
      // the following solution
      let len = this.form.plannerads.length;
      for (let i = 0; i < len; i++) {
        this.form.plannerads.pop();
      }
      len = this.form.plannergroups.length;
      for (let i = 0; i < len; i++) {
        this.form.plannergroups.pop();
      }
      this.form.plannertitle = "";
    },
    async deletePlanner(plannerid) {
      let err = await this.deletePlan(plannerid);
      this.notify(
        err,
        `Die Werbekampagne wurde erfolgreich gelöscht!`,
        `Die Werbekampagne konnte nicht gelöscht werden!`,
        this.$toast
      );
    },
    chkState(val) {
      const field = this.$v.form[val];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$error || field.$model === "");
    },
  },
  mounted: function () {
    if (!this.getAdvertisementStatus) {
      this.loadAdvertisements();
    }

    if (!this.getPublicitygroupStatus) {
      this.loadPublicitygroups();
    }

    if (!this.getPlanStatus) {
      this.loadPlans();
    }

    this.maxDate = this.getLicenseExpiration
      ? new Date(this.getLicenseExpiration)
      : new Date();
    this.licenseExpirationLoaded = true;
  },
  mixins: [NotificationMixin, validationMixin],
  data() {
    return {
      licenseExpirationLoaded: false,
      form: Object.assign({}, formShape),
      today: new Date(),
      confirmedAdvertisements: [],
      publicitygroups: [],
      minDate: new Date(tomorrow),
      maxDate: null,
      columns: [
        "plannertitle",
        "plannergroup",
        "planneradvertisement",
        "plannertype",
        "plannerstartdate",
        "plannerenddate",
        "settings",
        "plannerimpressionsday",
        "plannerimpressionsoverall",
        "plannerclicks",
        "planneroutreach",
        "plannermonitorinteractions",
        "plannerduration",
        "plannerstatus",
        "Publicity_id"
      ],
      // TODO: column classes for making leftmost column pinned
      columnsClasses: {
        plantitle: "table-absolute-position",
      },
      options: {
        headings: {
          plannertitle: "Titel",
          plannergroup: "Gruppe",
          planneradvertisement: "Werbung",
          plannertype: "Typ",
          plannerstartdate: "Startdatum",
          plannerenddate: "Enddatum",
          settings: "Einstellungen",
          plannerimpressionsday: "Impressionen (Heute)",
          plannerimpressionsoverall: "Impressionen (Gesamt)",
          plannerclicks: "Werbeklicks",
          planneroutreach: "Reichweite",
          plannermonitorinteractions: "Monitorinteraktionen",
          plannerduration: "Laufzeit (in Tagen)",
          plannerstatus: "Status",
          Publicity_id: "Kunden ID"
        },
        sortable: [
          "plannertitle",
          "plannergroup",
          "planneradvertisement",
          "plannertype",
          "plannerstartdate",
          "plannerenddate",
          "plannerimpressionsday",
          "plannerimpressionsoverall",
          "plannerclicks",
          "planneroutreach",
          "plannermonitorinteractions",
          "plannerduration",
        ],
        filterable: [
          "plannertitle",
          "plannergroup",
          "planneradvertisement",
          "plannertype",
          "plannerstartdate",
          "plannerenddate",
          "plannerimpressionsday",
          "plannerimpressionsoverall",
          "plannerclicks",
          "planneroutreach",
          "plannermonitorinteractions",
          "plannerduration",
          "plannerstatus",
        ],
        hiddenColumns: ["plannerstatus", "Publicity_id"],
        sortIcon: {
          base: "fa",
          up: "fa-sort-asc",
          down: "fa-sort-desc",
          is: "fa-sort",
        },
        pagination: {
          chunk: 5,
          edge: false,
          nav: "scroll",
        },
        texts: {
          filter: "Filter",
          filterPlaceholder: "Filtern nach ...",
          columns: "Spalten",
          limit: "Einträge",
          count: "Zeige Plan {from} bis {to} von {count} Einträgen",
          noResults: "Keine Einträge vorhanden",
        },
        rowAttributesCallback: (row) => {
          // ad campain running
          if (
            new Date(row.plannerstartdate) <= this.today &&
            new Date(row.plannerenddate) >= this.today
          ) {
            return { style: "background-color: #C1FB99" };
            // ad campaing starts in future
          } else if (
            new Date(row.plannerstartdate) > this.today &&
            new Date(row.plannerenddate) > this.today
          ) {
            return { style: "background-color: #F7F9C0" };
            // ad campaign already finished
          } else if (
            new Date(row.plannerstartdate) < this.today &&
            new Date(row.plannerenddate) < this.today
          ) {
            return { style: "background-color: #FAA0A0" };
          } else {
            return;
          }
        },
      },
      useVuex: false,
      theme: "bootstrap4",
      template: "default",
      feedBack: "secondary",
      submitted: false,
    };
  },
  validations: {
    form: {
      plannertitle: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(64),
      },
      plannerads: {
        required,
      },
      plannergroups: {
        required,
      },
    },
  },
  components: {
    ClientTable,
    Event,
    vSelect,
    "v-date-picker": DatePicker,
  },
};
</script>

<style>
.note {
  color: grey;
  font-size: small;
}
.req-field {
  font-size: 0.75em;
}
.planner-date-display {
  margin-left: 1vw;
}
.error-border {
  border: 1px solid red;
  border-radius: 5px;
}
.success-border {
  border: 1px solid #4dbd74;
  border-radius: 5px;
}
.table-absolute-position {
  position: absolute;
}
</style>

